'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function EngineHorsepowerCalculator() {
  const t = useTranslations("tools.engineHorsepowerCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // ET Method State
  const [etInputs, setEtInputs] = useState({
    weight: 5000,
    weightUnit: 'pound',
    time: 20,
    timeUnit: 'second'
  });

  // Trap-Speed Method State
  const [tsInputs, setTsInputs] = useState({
    weight: 5000,
    weightUnit: 'pound',
    speed: 70,
    speedUnit: 'mph'
  });

  const [results, setResults] = useState<{
    horsepower: number;
    method: string;
  } | null>(null);

  const [error, setError] = useState<string>('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Conversion Functions
  const convertWeight = (value: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'pound': 1,
      'ton': 2000,
      'kilogram': 2.20462
    };
    return value * conversions[unit];
  };

  const convertTime = (value: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'second': 1,
      'minute': 60
    };
    return value * conversions[unit];
  };

  const convertSpeed = (value: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'mph': 1,
      'kmph': 0.621371,
      'mps': 2.23694
    };
    return value * conversions[unit];
  };

  // Calculation Functions
  const calculateETHorsepower = (weightLbs: number, timeSeconds: number): number => {
    if (timeSeconds <= 0) {
      throw new Error(t('errorTimePositive'));
    }
    return weightLbs / Math.pow(timeSeconds / 5.825, 3);
  };

  const calculateTrapSpeedHorsepower = (weightLbs: number, speedMph: number): number => {
    if (speedMph <= 0) {
      throw new Error(t('errorSpeedPositive'));
    }
    return weightLbs * Math.pow(speedMph / 234, 3);
  };

  // Handle ET Method Calculation
  const handleETCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (etInputs.weight <= 0 || etInputs.time <= 0) {
        throw new Error(t('errorAllPositive'));
      }

      const weightLbs = convertWeight(etInputs.weight, etInputs.weightUnit);
      const timeSeconds = convertTime(etInputs.time, etInputs.timeUnit);

      const horsepower = calculateETHorsepower(weightLbs, timeSeconds);
      setResults({
        horsepower,
        method: t('etMethod')
      });

      setTimeout(() => {
        document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorCalculation'));
    }
  };

  // Handle Trap-Speed Method Calculation
  const handleTSCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (tsInputs.weight <= 0 || tsInputs.speed <= 0) {
        throw new Error(t('errorAllPositive'));
      }

      const weightLbs = convertWeight(tsInputs.weight, tsInputs.weightUnit);
      const speedMph = convertSpeed(tsInputs.speed, tsInputs.speedUnit);

      const horsepower = calculateTrapSpeedHorsepower(weightLbs, speedMph);
      setResults({
        horsepower,
        method: t('tsMethod')
      });

      setTimeout(() => {
        document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorCalculation'));
    }
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueLight: '#eff6ff',
    blueDark: '#1e40af',
    purple: '#7c3aed',
    yellow: '#f59e0b',
    yellowLight: '#fef3c7',
    yellowDark: '#78350f',
    gray: '#e2e8f0',
    grayLight: '#f8fafc',
    grayDark: '#475569',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    {}
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const methodsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const methodCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const methodTitleStyle = getResponsiveStyle(
    {
      color: colors.blue,
      fontWeight: 600,
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const formulaStyle = {
    background: colors.grayLight,
    borderLeft: `4px solid ${colors.purple}`,
    padding: '1rem',
    margin: '1rem 0',
    borderRadius: '0.5rem',
    fontFamily: "'Courier New', monospace",
    fontSize: '0.9rem',
    color: colors.grayDark,
  };

  const formGroupStyle = {
    marginBottom: '1.5rem',
  };

  const labelStyle = getResponsiveStyle(
    {
      display: 'block',
      color: colors.textPrimary,
      fontWeight: 500,
      marginBottom: '0.5rem'
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid ${colors.gray}`,
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem' },
    { padding: '0.75rem' },
    { padding: '0.75rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '0.5rem',
    },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.1rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      marginTop: '2rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      borderTop: `4px solid ${colors.yellow}`,
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.yellowLight} 0%, #fde68a 100%)`,
      borderRadius: '0.75rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderLeft: `4px solid ${colors.yellow}`,
    },
    { padding: '1rem', flexDirection: 'column' as const, gap: '0.5rem' },
    { padding: '1.25rem', flexDirection: 'row' as const },
    { padding: '1.25rem', flexDirection: 'row' as const }
  );

  const resultLabelStyle = getResponsiveStyle(
    {
      color: colors.yellowDark,
      fontWeight: 600,
    },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    {
      color: '#92400e',
      fontWeight: 700,
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const infoNoteStyle = getResponsiveStyle(
    {
      background: colors.blueLight,
      borderLeft: `4px solid #3b82f6`,
      borderRadius: '0.5rem',
      color: colors.blueDark,
      lineHeight: 1.6,
      marginTop: '1.5rem',
    },
    { padding: '0.875rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.9rem' }
  );

  const errorStyle = {
    background: '#fef2f2',
    borderLeft: '4px solid #ef4444',
    padding: '1rem',
    marginTop: '1rem',
    borderRadius: '0.5rem',
    color: '#991b1b',
    fontSize: '0.9rem',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    {
      marginBottom: '0.75rem',
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
          </div>

          <div style={methodsGridStyle}>
            {/* ET Method */}
            <div style={methodCardStyle}>
              <div style={methodTitleStyle}>
                ⏱️ {t('etMethodTitle')}
              </div>
              <div style={formulaStyle}>
                {t('etFormula')}
              </div>

              <form onSubmit={handleETCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('etWeightLabel')}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={etInputs.weight}
                      onChange={(e) => setEtInputs({...etInputs, weight: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      required
                    />
                    <select
                      value={etInputs.weightUnit}
                      onChange={(e) => setEtInputs({...etInputs, weightUnit: e.target.value})}
                      style={inputStyle}
                    >
                      <option value="pound">{t('unitPounds')}</option>
                      <option value="ton">{t('unitTons')}</option>
                      <option value="kilogram">{t('unitKilograms')}</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('etTimeLabel')}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={etInputs.time}
                      onChange={(e) => setEtInputs({...etInputs, time: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      required
                    />
                    <select
                      value={etInputs.timeUnit}
                      onChange={(e) => setEtInputs({...etInputs, timeUnit: e.target.value})}
                      style={inputStyle}
                    >
                      <option value="second">{t('unitSeconds')}</option>
                      <option value="minute">{t('unitMinutes')}</option>
                    </select>
                  </div>
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateETButton')}
                </button>
              </form>
            </div>

            {/* Trap-Speed Method */}
            <div style={methodCardStyle}>
              <div style={methodTitleStyle}>
                🚀 {t('tsMethodTitle')}
              </div>
              <div style={formulaStyle}>
                {t('tsFormula')}
              </div>

              <form onSubmit={handleTSCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('tsWeightLabel')}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={tsInputs.weight}
                      onChange={(e) => setTsInputs({...tsInputs, weight: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      required
                    />
                    <select
                      value={tsInputs.weightUnit}
                      onChange={(e) => setTsInputs({...tsInputs, weightUnit: e.target.value})}
                      style={inputStyle}
                    >
                      <option value="pound">{t('unitPounds')}</option>
                      <option value="ton">{t('unitTons')}</option>
                      <option value="kilogram">{t('unitKilograms')}</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('tsSpeedLabel')}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={tsInputs.speed}
                      onChange={(e) => setTsInputs({...tsInputs, speed: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      required
                    />
                    <select
                      value={tsInputs.speedUnit}
                      onChange={(e) => setTsInputs({...tsInputs, speedUnit: e.target.value})}
                      style={inputStyle}
                    >
                      <option value="mph">{t('unitMph')}</option>
                      <option value="kmph">{t('unitKmph')}</option>
                      <option value="mps">{t('unitMps')}</option>
                    </select>
                  </div>
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateTSButton')}
                </button>
              </form>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={errorStyle}>
              {error}
            </div>
          )}

          {/* Results */}
          {results && (
            <div id="results" style={resultsStyle}>
              <h3 style={h2Style}>📊 {t('resultsTitle')}</h3>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{results.method}:</span>
                <span style={resultValueStyle}>{results.horsepower.toFixed(2)} HP</span>
              </div>
              <div style={infoNoteStyle}>
                <strong>{t('noteLabel')}</strong> {t('noteText')}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insight1')}</li>
          <li style={listItemStyle}>{t('insight2')}</li>
          <li style={listItemStyle}>{t('insight3')}</li>
          <li style={listItemStyle}>{t('insight4')}</li>
          <li style={listItemStyle}>{t('insight5')}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practice1')}</li>
          <li style={listItemStyle}>{t('practice2')}</li>
          <li style={listItemStyle}>{t('practice3')}</li>
          <li style={listItemStyle}>{t('practice4')}</li>
          <li style={listItemStyle}>{t('practice5')}</li>
        </ul>
      </section>

      {/* Understanding Horsepower */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('understandingTitle')}</h2>
        <p style={paragraphStyle}>{t('understandingParagraph1')}</p>
        <p style={paragraphStyle}>{t('understandingParagraph2')}</p>
        <p style={paragraphStyle}>{t('understandingParagraph3')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.inchcalculator.com/engine-horsepower-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.redexadditives.com/blog/how-to-calculate-your-cars-horsepower-and-everything-else-you-need-to-know-about-hp/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.techtarget.com/whatis/definition/horsepower-hp" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.yanmartractor.com/resources/tractor-tips/what-does-horsepower-really-mean/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://powertestdyno.com/how-to-calculate-horsepower/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}